import Image from "@Components/Image";
import { AppState } from "@Store/index";
import { useDispatch, useSelector } from "react-redux";
import style from "./AddReview.module.scss";
import AddReviewForm1 from "./AddReviewForms/AddReviewForm1";
import PropertyDetailsModal from "../../PropertyDetailsModal";
import { useEffect, useRef, useState } from "react";
import { uploadReview, updateReviews } from "../data/helper";
import AddReviewForm2 from "./AddReviewForms/AddReviewForm2";
import { useHistory } from "hooks/useHistory";
import { useLocation } from "react-router";
import AddReviewFormSuccess from "./AddReviewForms/AddReviewFormSuccess";
import wrapperHOC from "@Utils/wrapperHOC";
import LoaderIcon from "@Icons/LoaderIcon";
import Modal from "@Components/Modal";
import Button from "@Components/Button";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import { getLocation } from "@Pages/PropertyPage/data/helper";
import AppPromotionSmartBanner from "@Components/AppPromotionSmartBanner";
import { REVIEW_COLLECTION } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import LoginDispatcher from "@Components/Login/actions";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";
import InventoryCard from "@Components/FormLayout/InventoryCard";

import ReviewUserDetails from "@Pages/UserReviewScreen/ReviewUserDetails";
import getInventoryReviewData from "@Pages/PropertyPage/data/getInventoryReviewData";
import useIsApp from "hooks/useIsApp";
import BreadCrumbs from "@Components/BreadCrumbs";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";

const AddReview = ({
  isDesktop = false,
  userBooking = {},
  asPage = false,
  hidePropertyHeaderOnPage = false,
  bookingSummary = null,
  bookingId,
  reviewUserContext,
  overrideSummary = null,
  onPageChange,
  cb = "",
}: any) => {
  const { name, id, data, userData, propertyLocation, isUserLoggedIn } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      data: propertyPageData?.data || userBooking,
      name: propertyPageData?.data?.name || userBooking?.name,
      id: propertyPageData?.data?.id || userBooking?.id,
      userData: appData?.userData,
      propertyLocation: propertyPageData?.data?.location || userBooking?.location,
      isUserLoggedIn: appData?.isUserLoggedIn,
    }),
  );

  const { t } = useTranslationHook();
  const location = useLocation();
  const history = useHistory();
  const initialUserDetails: any = (userData as any)?.data ?? userData ?? null;

  // Check if user is coming from profile booking route (without encrypted user_id)
  const isFromProfileBooking =
    location.pathname.startsWith("/profile/review/upload/") &&
    !new URLSearchParams(location.search).has("user");
  const [review, setReview]: any = useState({ user_details: initialUserDetails });
  const [showError, setShowError]: any = useState(false);
  const [disableActionButton, setDisableActionButton]: any = useState(true);
  const [, setHasImages]: any = useState(false);
  const [pageNumber, setPageNumber]: any = useState(1);
  const [actionButtonText, setActionButtonText]: any = useState(
    t("propertypage.reviewssection.addreview.submitreviewbutton"),
  );
  const [showModal, setShowModal]: any = useState(true);
  const reviewId = useRef(null);
  const containerRef = useRef<any>(null);
  const isApp = useIsApp();

  const [hasExistingReview, setHasExistingReview] = useState(false);
  const lastReviewCheckKeyRef = useRef<string | null>(null);
  const [isEditingExistingReview, setIsEditingExistingReview] = useState(true);

  useEffect(() => {
    const imagesExist =
      (review?.images && review.images.length > 0) || (review?.videos && review.videos.length > 0);
    setHasImages(imagesExist);
  }, [review?.images, review?.videos]);

  // Check if all required fields are filled
  useEffect(() => {
    const hasRating = review?.rating && review.rating > 0;
    const hasContent = review?.content && String(review.content).trim() !== "";
    // const hasImagesUploaded = review?.images && review.images.length > 0;

    setDisableActionButton(!(hasRating && hasContent));
  }, [review?.rating, review?.content, review?.images]);

  useEffect(() => {
    if (onPageChange) {
      onPageChange(pageNumber);
    }
  }, [pageNumber, onPageChange]);

  const pLocation: any = getLocation(propertyLocation, [
    "route_short_name",
    "locality_long_name",
    "postal_code",
    "country_long_name",
  ]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  // for checking if user is loggedin before adding review
  const dispatch = useDispatch();
  const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
  const handleLoginClick = () => {
    const loginDispatcher = new LoginDispatcher(dispatch);
    setShowModal(false);
    loginDispatcher.showLoginModal(isApp ? { screenName: APP_PAGE.PROPERTY } : {});
  };

  const submitReview = async () => {
    if (pageNumber == 1) {
      if (!review["rating"]) {
        setShowError(true);
      } else if (!review?.content || String(review?.content).trim() === "") {
        toastNotificationDispatcher.openToast({
          title: t("propertypage.reviewssection.addreview.errortitle"),
          message: t("propertypage.reviewssection.addreview.pleaseaddcommentmessage"),
          type: "error",
          color: "red",
        });
      } else {
        setDisableActionButton(true);
        setActionButtonText(<LoaderIcon fill="#fff" />);
        const enrichedPayload = {
          ...review,
          ...(reviewUserContext?.userId !== undefined && reviewUserContext?.userId !== null
            ? { user_id: reviewUserContext.userId }
            : {}),
          ...(reviewUserContext?.userDetails
            ? { user_details: reviewUserContext.userDetails }
            : {}),
        };
        if (
          enrichedPayload?.user_id === undefined ||
          enrichedPayload?.user_id === null ||
          enrichedPayload?.user_id === ""
        ) {
          const fallbackUserId = (review as any)?.user_id ?? (review as any)?.user_details?.id;
          if (fallbackUserId !== undefined && fallbackUserId !== null && fallbackUserId !== "") {
            (enrichedPayload as any).user_id = fallbackUserId;
          }
        }
        const isEditing = Boolean(hasExistingReview && reviewId.current);
        const res = isEditing
          ? await updateReviews(enrichedPayload, {}, id, reviewId.current)
          : await uploadReview(enrichedPayload, {}, id);
        reviewId.current = res?.id;

        setPageNumber(3);
        setActionButtonText("Return to Home Page");
        setDisableActionButton(false);

        gtagEventEmitter({
          action: "review_submitted",
          platform: "amber-user-website",
          source: "email",
          page: 1,
        });
      }
    } else if (asPage) {
      history.push("/");
    } else {
      handleModalClose();
    }
  };

  useEffect(() => {
    if (pageNumber === 3) {
      containerRef?.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [pageNumber]);

  useEffect(() => {
    const queryString = location.search;
    const params = new URLSearchParams(queryString);
    const userName = params.get("userName")?.trim();
    const email = params.get("emailId")?.trim();
    const encryptedUserToken = params.get("user")?.trim();

    const derivedUserId =
      (reviewUserContext as any)?.userId ??
      (reviewUserContext as any)?.userDetails?.id ??
      (userData as any)?.id ??
      (userData as any)?.data?.id ??
      (initialUserDetails as any)?.id;

    let updatedDetails: Record<string, unknown> | null = null;
    if (userName && email) {
      updatedDetails = { ...(initialUserDetails || {}), name: userName, email } as Record<
        string,
        unknown
      >;
    } else if (initialUserDetails) {
      updatedDetails = initialUserDetails as Record<string, unknown>;
    }

    const hasUserId = Boolean(derivedUserId);
    const hasUserDetails = Boolean(updatedDetails && Object.keys(updatedDetails).length > 0);

    if (hasUserId || hasUserDetails || encryptedUserToken) {
      setReview((prevReview: any) => ({
        ...prevReview,
        ...(hasUserId ? { user_id: derivedUserId } : {}),
        ...(hasUserDetails ? { user_details: updatedDetails } : {}),
        ...(encryptedUserToken ? { user: encryptedUserToken, is_encrypted_user_id: true } : {}),
      }));
    }

    if (!encryptedUserToken && !isUserLoggedIn) {
      handleLoginClick();
    }
  }, [location.search, userData, isUserLoggedIn, reviewUserContext]);

  useEffect(() => {
    if (!reviewUserContext) return;
    setReview((prev: any) => ({
      ...prev,
      ...(reviewUserContext.userId || (reviewUserContext as any)?.userDetails?.id
        ? { user_id: reviewUserContext.userId || (reviewUserContext as any)?.userDetails?.id }
        : {}),
      ...(reviewUserContext.userDetails ? { user_details: reviewUserContext.userDetails } : {}),
      is_encrypted_user_id: true,
    }));
  }, [reviewUserContext]);

  useEffect(() => {
    if (bookingId) {
      setReview((prev: any) => ({ ...prev, booking_id: bookingId }));
    }
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        if (!id) return;

        const key = `${id}`;
        if (lastReviewCheckKeyRef.current === key) return;
        lastReviewCheckKeyRef.current = key;

        const queryString = location.search;
        const params = new URLSearchParams(queryString);
        const userHash = params.get("user")?.trim();

        const resp = await getInventoryReviewData({
          id,
          page: 1,
          limit: 50,
          params: { status: "any" },
          selectedLang: "en",
          user_encrypted: Boolean(userHash),
          user: userHash,
        });

        // Check if there are any reviews (count >= 1)
        const reviewCount = resp?.data?.data?.meta?.count || 0;
        const hasReviews = reviewCount >= 1;

        if (!cancelled) setHasExistingReview(hasReviews);

        if (!cancelled && hasReviews) {
          const firstReview = resp?.data?.data?.result?.[0] || null;
          if (firstReview) {
            setReview((prev: any) => ({
              ...prev,
              ...(firstReview.rating !== undefined ? { rating: firstReview.rating } : {}),
              ...(firstReview.content !== undefined ? { content: firstReview.content } : {}),
              ...(firstReview.images !== undefined ? { images: firstReview.images } : {}),
              ...(firstReview.videos !== undefined ? { videos: firstReview.videos } : {}),
              ...(firstReview.rating_details !== undefined
                ? { rating_details: firstReview.rating_details }
                : {}),
            }));
            reviewId.current = (firstReview as any)?.id ?? null;
            setActionButtonText("propertypage.reviewssection.addreview.updatereviewbutton");
            setDisableActionButton(false);
          }
        }
      } catch (_e) {
        if (!cancelled) setHasExistingReview(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [id, location.search]);

  useEffect(() => {
    setIsEditingExistingReview(!hasExistingReview);
    if (cb) cb(hasExistingReview);
  }, [hasExistingReview]);

  const shouldShowSubmittedInfo = hasExistingReview && !isEditingExistingReview && pageNumber !== 3;

  const SubmittedInfo = () => (
    <div className={style.submittedContainer}>
      <div className={style.submittedCard}>
        <div className={style.submittedTitle}>
          {t("propertypage.reviewssection.addreview.yourresponserecorded")}
        </div>
        {/* Temporarily commented out - edit response functionality 
        <button
          type="button"
          className={style.submittedLink}
          onClick={() => {
            setIsEditingExistingReview(true);
            containerRef?.current?.scrollTo?.({ top: 0, behavior: "smooth" });
          }}
        >
          Edit your response
        </button>
        */}
      </div>
    </div>
  );

  let content: JSX.Element | null = null;

  if (asPage) {
    content = (
      <div
        ref={containerRef}
        className={`${style.pageWrapper} ${
          pageNumber === 3 || shouldShowSubmittedInfo ? style.pageWrapperSuccess : ""
        } ${!isDesktop ? style.mobile : ""}`}
      >
        <div className={style.pageHeaderSection}>
          <div className={`${style.reviewTitle} ${!isDesktop ? style.mobileHidden : ""}`}>
            Add Review
          </div>
          {isFromProfileBooking && (
            <div className={style.breadcrumbContainer}>
              <BreadCrumbs
                data={[
                  { link: "/", name: "Home" },
                  { link: "/profile", name: "Profile" },
                  { link: "/profile/leads", name: "Booking" },
                  { link: "#", name: "Add Review" },
                ]}
                className={style.breadcrumb}
              />
            </div>
          )}
        </div>
        <div className={style.mainContentContainer}>
          <div
            className={`${style.asPageRow} ${
              pageNumber === 3 || shouldShowSubmittedInfo ? style.asPageRowSuccess : ""
            }`}
          >
            <div className={style.asPageLeftColumn}>
              {pageNumber != 3 && (
                <div className={style.bookingDetailsSection}>
                  <ReviewUserDetails
                    userName={bookingSummary?.userName || review?.user_details?.name}
                    email={bookingSummary?.email || review?.user_details?.email}
                    phoneNumber={bookingSummary?.phoneNumber || review?.user_details?.phone}
                  />
                </div>
              )}
              {pageNumber != 3 &&
                !hidePropertyHeaderOnPage &&
                !shouldShowSubmittedInfo &&
                !isDesktop && (
                  <div className={style.propertyDetailsContainer}>
                    <Image
                      className={style.featuredImage}
                      src={data?.images[0]?.path}
                      alt="review"
                      width="100%"
                      height="100%"
                    />
                    <div className={style.propertyDetails}>
                      <div className={style.propertyName}>{name}</div>
                      <div className={style.propertyRegion}>{pLocation}</div>
                      {!isDesktop && (
                        <>
                          {overrideSummary?.price && (
                            <div className={style.mobileRentValue}>{overrideSummary.price}</div>
                          )}
                          <div className={style.mobileSeparatorLine} />
                          <div className={style.mobileBookingDetails}>
                            {overrideSummary?.roomName && (
                              <div className={style.mobileRoomType}>{overrideSummary.roomName}</div>
                            )}
                            <div className={style.mobileBookingRow}>
                              {overrideSummary?.duration && (
                                <div className={style.mobileBookingValue}>
                                  {overrideSummary.duration}
                                </div>
                              )}
                              {overrideSummary?.duration && overrideSummary?.moveInDate && (
                                <span className={style.mobileBullet}>•</span>
                              )}
                              {overrideSummary?.moveInDate && (
                                <div className={style.mobileBookingValue}>
                                  {config.IS_CHINA_APP
                                    ? renderChinaNamedDate(
                                        overrideSummary.moveInDate,
                                        overrideSummary.moveInDate,
                                      )
                                    : overrideSummary.moveInDate}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              {shouldShowSubmittedInfo && <SubmittedInfo />}
              {!shouldShowSubmittedInfo && pageNumber == 1 && (
                <AddReviewForm1
                  review={review}
                  setReview={setReview}
                  showError={showError}
                  setShowError={setShowError}
                  isDesktop={isDesktop}
                  hasExistingReview={hasExistingReview}
                />
              )}
              {!shouldShowSubmittedInfo && pageNumber == 2 && (
                <AddReviewForm2 review={review} setReview={setReview} />
              )}
              {pageNumber == 3 && <AddReviewFormSuccess name={review?.user_details?.name} />}
              {!shouldShowSubmittedInfo && (
                <div className={style.addReviewButton}>
                  <Button
                    disable={disableActionButton}
                    variant="primary"
                    className={style.addReviewSubmitBtn}
                    type="button"
                    onClick={submitReview}
                  >
                    {actionButtonText}
                  </Button>
                </div>
              )}
            </div>
            {isDesktop && pageNumber !== 3 && !shouldShowSubmittedInfo && (
              <div className={style.asPageRightColumn}>
                <InventoryCard
                  isSoldOut={false}
                  inventory={data}
                  isConfirmation={false}
                  onlyView
                  isPaymentPage={false}
                  bookFormPrefix="review"
                  overrideSummary={overrideSummary}
                  useOverrideSummaryOnly
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else if (isDesktop) {
    content = (
      <PropertyDetailsModal
        hidePropertyModal={handleModalClose}
        reducedZIndex
        isDesktop={isDesktop}
        header={<span className={style.reviewTitle}>Add Review</span>}
        actionButtonText={actionButtonText}
        actionButtonClick={submitReview}
        disableActionButton={shouldShowSubmittedInfo ? true : disableActionButton}
      >
        <div className={style.bookingDetailsSection}>
          <ReviewUserDetails
            userName={review?.user_details?.name}
            email={review?.user_details?.email}
            phoneNumber={review?.user_details?.phone}
          />
        </div>
        <div>
          {/* Removed inline edit banner */}
          {pageNumber != 3 && !shouldShowSubmittedInfo && (
            <div className={style.propertyDetailsContainer}>
              <Image
                className={style.featuredImage}
                src={data?.images[0]?.path}
                alt="review"
                width="100%"
                height="100%"
              />
              <div className={style.propertyDetails}>
                <div className={style.propertyName}>{name}</div>
                <div className={style.propertyRegion}>{pLocation}</div>
              </div>
            </div>
          )}
          {shouldShowSubmittedInfo && <SubmittedInfo />}
          {!shouldShowSubmittedInfo && pageNumber == 1 && (
            <AddReviewForm1
              review={review}
              setReview={setReview}
              showError={showError}
              setShowError={setShowError}
              isDesktop
            />
          )}
          {!shouldShowSubmittedInfo && pageNumber == 2 && (
            <AddReviewForm2 review={review} setReview={setReview} />
          )}
          {pageNumber == 3 && <AddReviewFormSuccess name={review?.user_details?.name} />}
        </div>
      </PropertyDetailsModal>
    );
  } else {
    content = (
      <Modal
        hideModal={handleModalClose}
        className="src-components-Modal-Modal-module__modalReview"
        title="Add Review"
        modalRef={containerRef}
      >
        <div className={style.modalContainerReview}>
          <div className={style.bookingDetailsSection}>
            <ReviewUserDetails
              userName={review?.user_details?.name}
              email={review?.user_details?.email}
              phoneNumber={review?.user_details?.phone}
            />
          </div>
          {pageNumber != 3 && !shouldShowSubmittedInfo && (
            <div className={style.propertyDetailsContainer}>
              <Image
                className={style.featuredImage}
                src={data?.images[0]?.path}
                alt="review"
                width="100%"
                height="100%"
              />
              <div className={style.propertyDetails}>
                <div className={style.propertyName}>{name}</div>
                <div className={style.propertyRegion}>{pLocation}</div>
              </div>
            </div>
          )}
          {shouldShowSubmittedInfo && <SubmittedInfo />}
          {!shouldShowSubmittedInfo && pageNumber == 1 && (
            <AddReviewForm1
              review={review}
              setReview={setReview}
              showError={showError}
              setShowError={setShowError}
              hasExistingReview={hasExistingReview}
            />
          )}
          {!shouldShowSubmittedInfo && pageNumber == 2 && (
            <AddReviewForm2 review={review} setReview={setReview} />
          )}
          {pageNumber == 3 && <AddReviewFormSuccess name={review?.user_details?.name} />}
          {/* Removed inline edit banner */}
        </div>
        {!shouldShowSubmittedInfo && (
          <div className={style.modalFooter}>
            <Button
              disable={disableActionButton}
              variant="primary"
              className={style.addReviewSubmitBtn}
              type="button"
              onClick={submitReview}
            >
              {actionButtonText}
            </Button>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <>
      {showModal && <>{content}</>}
      <AppPromotionSmartBanner from={REVIEW_COLLECTION} />
    </>
  );
};

export default wrapperHOC(AddReview, {
  componentName: "AddReview-ReviewsSection",
  showForChina: true,
});
